/**
 * Data layouts — the data panel's views as cells of a layout's grid
 * (docs/features/pip-and-data-panel.md, *Data layouts*).
 *
 * A view is a section of kind `dataView`, so a grid of them is an ordinary
 * layout: arrangements, divider drags, saving, the tab and its pop-out all come
 * from the layout. What is particular to them is here, pure: which views exist,
 * the rule against repeats, and the edits a cell's ▾ makes.
 */

import { mintUlid, type DataViewConfig, type Layout, type Section } from "@qufolio/model";

import { dataViewConfig } from "./section-config";

export type DataView = NonNullable<DataViewConfig["view"]>;

/**
 * The views a cell can show, in the panel's tab order. AI Review is not one —
 * a tab only (owner, 2026-10-29).
 */
export const DATA_VIEWS: readonly DataView[] = [
  "profile",
  "peers",
  "fundamentals",
  "news",
  "sentiment",
  "seasonality",
  "barIntel",
  "holdings",
  "congress",
];

/**
 * What makes two cells the same, for the no-repeats rule: the view — and for
 * Fin St its grain too, because one cell by fiscal year beside one by quarter
 * is the point of having two (owner, 2026-10-29). An unchosen cell is `null`
 * and never clashes.
 */
export function cellKey(config: DataViewConfig | undefined): string | null {
  if (!config?.view) return null;
  return config.view === "fundamentals" ? `fundamentals:${config.finStGrain ?? "years"}` : config.view;
}

/** The keys the other cells of a layout already hold — what ▾ greys out. */
export function keysInUse(layout: Layout, exceptSectionId?: string): Set<string> {
  const keys = new Set<string>();
  for (const s of layout.sections) {
    if (s.id === exceptSectionId) continue;
    const key = cellKey(dataViewConfig(s));
    if (key) keys.add(key);
  }
  return keys;
}

/** A layout made only of data cells — the one with nothing on screen to follow. */
export function isDataOnly(layout: Layout | undefined): boolean {
  if (!layout) return false;
  const cells = layout.sections.filter((s) => s.kind === "chartGroup" || s.kind === "dataView");
  return cells.length > 0 && cells.every((s) => s.kind === "dataView");
}

/**
 * ✎ › New data layout: 3×3, the nine views in tab order. Fin St is by fiscal
 * year; a second, by quarter, is one ▾ away.
 */
export function dataLayout(name: string, mint: () => string = mintUlid): Layout {
  return {
    id: mint(),
    name,
    rows: 3,
    columns: 3,
    rowSizes: ["1fr", "1fr", "1fr"],
    columnSizes: ["1fr", "1fr", "1fr"],
    sections: DATA_VIEWS.map((view, i) => ({
      id: mint(),
      kind: "dataView",
      row: Math.floor(i / 3),
      column: i % 3,
      config: { view },
    })),
  };
}

/**
 * The layout a data layout follows, if one (owner, 2026-10-29) — read off its
 * cells, where it is kept so it needs no column in qv-data. Every cell carries
 * it, but a cell added by an arrangement starts bare, so any cell's answer is
 * the layout's. `undefined` is the default: the chart clicked last.
 */
export function followOf(layout: Layout | undefined): string | undefined {
  for (const s of layout?.sections ?? []) {
    const id = dataViewConfig(s)?.followLayoutId;
    if (id) return id;
  }
  return undefined;
}

/** Follow one layout (`null`: the chart clicked last), set on every data cell together. */
export function setFollow(layout: Layout, layoutId: string | null): Layout {
  return {
    ...layout,
    sections: layout.sections.map((s) => {
      const config = dataViewConfig(s);
      if (!config) return s;
      const { followLayoutId: _old, ...rest } = config;
      return { ...s, config: layoutId ? { ...rest, followLayoutId: layoutId } : rest };
    }),
  };
}

/** What a cell's ▾ asks for: a view (with Fin St's grain), or a chart. */
export type CellChoice = { view: DataView; finStGrain?: "years" | "quarters" } | "chart";

/**
 * The layout with one cell changed — kind and config replaced, its place and
 * id kept, so the grid does not move and the cell is the same cell.
 *
 * A choice already held by another cell is refused (the layout comes back
 * unchanged): ▾ greys it out, and this is what keeps the rule true for any
 * other caller too.
 */
export function setCell(layout: Layout, sectionId: string, choice: CellChoice, makeChart: () => Section): Layout {
  if (choice !== "chart" && keysInUse(layout, sectionId).has(cellKey(choice)!)) return layout;
  // A changed cell keeps following what the layout follows.
  const follow = followOf(layout);
  return {
    ...layout,
    sections: layout.sections.map((s) => {
      if (s.id !== sectionId) return s;
      const place = {
        id: s.id,
        row: s.row,
        column: s.column,
        ...(s.rowSpan !== undefined ? { rowSpan: s.rowSpan } : {}),
        ...(s.columnSpan !== undefined ? { columnSpan: s.columnSpan } : {}),
      };
      if (choice === "chart") return { ...makeChart(), ...place };
      const config: DataViewConfig = {
        view: choice.view,
        ...(choice.view === "fundamentals" ? { finStGrain: choice.finStGrain ?? "years" } : {}),
        ...(follow ? { followLayoutId: follow } : {}),
      };
      return { ...place, kind: "dataView", config };
    }),
  };
}
